import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectBrandingApi } from '../services/projectBrandingApi';
import type { ProjectBranding, ProjectBrandingUpdate } from '../types/projectBranding';

export const projectBrandingKeys = {
  all: ['project-branding'] as const,
  detail: (projectId: string) => [...projectBrandingKeys.all, projectId] as const,
};

export function useProjectBranding(projectId: string | undefined) {
  return useQuery({
    queryKey: projectBrandingKeys.detail(projectId!),
    queryFn: async (): Promise<ProjectBranding> => {
      const response = await projectBrandingApi.get(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load branding');
      }
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useUpdateProjectBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, data }: { projectId: string; data: ProjectBrandingUpdate }) => {
      const response = await projectBrandingApi.update(projectId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update branding');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectBrandingKeys.detail(variables.projectId),
      });
    },
  });
}

export function useUploadProjectLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, file }: { projectId: string; file: File }) => {
      const response = await projectBrandingApi.uploadLogo(projectId, file);
      if (!response.success) {
        throw new Error(response.error || 'Failed to upload logo');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectBrandingKeys.detail(variables.projectId),
      });
    },
  });
}

export function useRemoveProjectLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      const response = await projectBrandingApi.removeLogo(projectId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove logo');
      }
      return response.data;
    },
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({
        queryKey: projectBrandingKeys.detail(projectId),
      });
    },
  });
}
